import { create } from 'zustand';
import type { CartItem, Artwork, Language, Currency, FrameOption } from '@/types';

// ============================================================================
// CART STORE
// ============================================================================

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  addItem: (artwork: Artwork, frame: FrameOption, quantity?: number) => void;
  removeItem: (artworkId: string) => void;
  updateQuantity: (artworkId: string, quantity: number) => void;
  updateFrame: (artworkId: string, frame: FrameOption) => void;
  clearCart: () => void;
  toggleCart: () => void;
  totalItems: () => number;
  subtotal: () => number;
  hydrateImages: () => void;
}

/**
 * Frame markup for an artwork.
 *
 * The precedence must match the artwork detail page AND the server
 * (per-artwork percentage → global multiplier → none). If it drifts, the cart quotes one
 * price while checkout charges the server-computed one.
 */
function getFrameMultiplier(artwork: Artwork, frame: FrameOption): number {
  if (frame === 'unframed') return 1;
  try {
    const perArtwork = artwork.frameMarkups?.[frame as 'black' | 'white' | 'custom'];
    if (perArtwork != null && perArtwork > 0) return 1 + perArtwork / 100;

    const settings = (window as unknown as { __ADMIN_SETTINGS__?: { frameMultipliers?: Record<string, number> } }).__ADMIN_SETTINGS__;
    const mults = settings?.frameMultipliers;
    if (mults && typeof mults[frame] === 'number') return mults[frame] || 1;
  } catch { /* ignore */ }
  return 1;
}

function calculateItemPrice(artwork: Artwork, frame: FrameOption, quantity: number): number {
  const basePrice = Math.round(artwork.price.amount * getFrameMultiplier(artwork, frame));
  return basePrice * quantity;
}

interface SavedCartItem {
  artworkId: string;
  title: { en: string; ka: string };
  price: number;
  currency: string;
  frame: FrameOption;
  quantity: number;
  totalPrice: number;
  category: string;
  slug?: string;
  /** Persisted so a quantity change after a page reload still applies the same markup. */
  frameMarkups?: { black?: number; white?: number; custom?: number };
}

function loadCartItems(): CartItem[] {
  try {
    const raw = localStorage.getItem('gallery-cart-v2');
    if (!raw) return [];
    const saved = JSON.parse(raw) as SavedCartItem[];
    if (!Array.isArray(saved)) return [];
    return saved.map((s) => ({
      artwork: {
        _id: s.artworkId,
        title: s.title || { en: '', ka: '' },
        description: { en: '', ka: '' },
        images: [],
        category: s.category || '',
        medium: { en: '', ka: '' },
        year: 0,
        dimensions: { width: 0, height: 0, unit: 'cm' as const },
        sizeClass: 'medium' as const,
        price: { amount: s.price, currency: (s.currency || 'USD') as Currency },
        frameMarkups: s.frameMarkups,
        rarity: 'limited' as const,
        artist: '',
        slug: s.slug || '',
        isActive: true,
        createdAt: '',
        updatedAt: '',
      } as Artwork,
      frame: s.frame,
      quantity: s.quantity,
      totalPrice: s.totalPrice,
    }));
  } catch { /* ignore */ }
  return [];
}

function saveCartItems(items: CartItem[]): void {
  try {
    const lite: SavedCartItem[] = items.map((item) => ({
      artworkId: item.artwork._id,
      title: { en: item.artwork.title?.en || '', ka: item.artwork.title?.ka || '' },
      price: item.artwork.price.amount,
      currency: item.artwork.price.currency,
      frame: item.frame,
      quantity: item.quantity,
      totalPrice: item.totalPrice,
      category: item.artwork.category || '',
      slug: (item.artwork as Record<string, unknown>).slug as string || '',
      frameMarkups: item.artwork.frameMarkups,
    }));
    localStorage.setItem('gallery-cart-v2', JSON.stringify(lite));
  } catch { /* quota exceeded */ }
}

export const useCartStore = create<CartState>((set, get) => ({
  items: loadCartItems(),
  isOpen: false,

  addItem: (artwork, frame, quantity = 1) => {
    set((state) => {
      const existing = state.items.find(
        (i) => i.artwork._id === artwork._id && i.frame === frame,
      );
      let newItems: CartItem[];
      if (existing) {
        newItems = state.items.map((i) =>
          i.artwork._id === artwork._id && i.frame === frame
            ? { ...i, quantity: i.quantity + quantity, totalPrice: calculateItemPrice(artwork, frame, i.quantity + quantity) }
            : i,
        );
      } else {
        newItems = [...state.items, { artwork, quantity, frame, totalPrice: calculateItemPrice(artwork, frame, quantity) }];
      }
      saveCartItems(newItems);
      return { items: newItems };
    });
  },

  removeItem: (artworkId) => {
    set((state) => {
      const newItems = state.items.filter((i) => i.artwork._id !== artworkId);
      saveCartItems(newItems);
      return { items: newItems };
    });
  },

  updateQuantity: (artworkId, quantity) => {
    if (quantity < 1) return;
    set((state) => {
      const newItems = state.items.map((i) =>
        i.artwork._id === artworkId
          ? { ...i, quantity, totalPrice: calculateItemPrice(i.artwork, i.frame, quantity) }
          : i,
      );
      saveCartItems(newItems);
      return { items: newItems };
    });
  },

  updateFrame: (artworkId, frame) => {
    set((state) => {
      const newItems = state.items.map((i) =>
        i.artwork._id === artworkId
          ? { ...i, frame, totalPrice: calculateItemPrice(i.artwork, frame, i.quantity) }
          : i,
      );
      saveCartItems(newItems);
      return { items: newItems };
    });
  },

  clearCart: () => { saveCartItems([]); set({ items: [] }); },
  toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),
  totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
  subtotal: () => get().items.reduce((sum, i) => sum + i.totalPrice, 0),

  hydrateImages: () => {
    const items = get().items;
    const needsImages = items.some((i) => !i.artwork.images?.length || !i.artwork.images[0]);
    if (!needsImages) return;
    const ids = items.map((i) => i.artwork._id);
    fetch('/api/v1/artworks?images=true')
      .then((r) => r.json())
      .then((res: { success: boolean; data: Artwork[] }) => {
        if (!res.success || !Array.isArray(res.data)) return;
        const artMap = new Map<string, Artwork>();
        res.data.forEach((a) => artMap.set(a._id, a));
        set((state) => ({
          items: state.items.map((item) => {
            const fresh = artMap.get(item.artwork._id);
            if (fresh) {
              return { ...item, artwork: { ...item.artwork, images: fresh.images || [], artist: fresh.artist } };
            }
            return item;
          }),
        }));
      })
      .catch(() => {});
  },
}));

// Hydrate cart images on load
if (useCartStore.getState().items.length > 0) {
  useCartStore.getState().hydrateImages();
}

// ============================================================================
// APP STORE (theme, language, currency)
// ============================================================================

interface AppState {
  isDarkMode: boolean;
  language: Language;
  currency: Currency;
  exchangeRate: number;
  exchangeRateDate: string;
  toggleTheme: () => void;
  setLanguage: (lang: Language) => void;
  setCurrency: (cur: Currency) => void;
  setExchangeRate: (rate: number, date?: string) => void;
}

function loadCachedRate(): number {
  try {
    const raw = localStorage.getItem('gallery-nbg-rate');
    if (raw) {
      const { rate } = JSON.parse(raw) as { rate: number };
      if (typeof rate === 'number' && rate > 0) return rate;
    }
  } catch { /* use fallback */ }
  return 2.67;
}

export const useAppStore = create<AppState>((set) => ({
  isDarkMode: localStorage.getItem('gallery-theme') === 'dark',
  language: (localStorage.getItem('gallery-lang') as Language) || 'en',
  currency: (localStorage.getItem('gallery-currency') as Currency) || 'USD',
  exchangeRate: loadCachedRate(),
  exchangeRateDate: '',

  toggleTheme: () =>
    set((state) => {
      const next = !state.isDarkMode;
      localStorage.setItem('gallery-theme', next ? 'dark' : 'light');
      if (next) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return { isDarkMode: next };
    }),

  setLanguage: (lang) => {
    localStorage.setItem('gallery-lang', lang);
    set({ language: lang });
  },

  setCurrency: (cur) => {
    localStorage.setItem('gallery-currency', cur);
    set({ currency: cur });
  },

  setExchangeRate: (rate, date) => set({ exchangeRate: rate, exchangeRateDate: date || '' }),
}));
